<script setup lang="ts">
import CookieConsentSettings from "@/components/common/Integration/CookieConsentSettings.vue";
import config from "@/config";
import { getFormEmbedUrlForRegion } from "@/config/regionalPreviewUrl";
import { useEmbedSettings } from "@/composables/useEmbedSettings";
import { useAppStore } from "@/store/app";
import {
  buildBasicEmbedCode,
  type EmbedSettings,
} from "@/util/embedCode";
import { UITabPane, UITabs } from "@gohighlevel/ghl-ui";
import { computed, nextTick, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import EmbedAdvancedSettings from "./EmbedAdvancedSettings.vue";
import EmbedCodeModal from "./EmbedCodeModal.vue";
import EmbedGeneralSettings from "./EmbedGeneralSettings.vue";
import EmbedPreview from "./EmbedPreview.vue";

const controls = useEmbedSettings();
const store = useAppStore();
const { t } = useI18n();
const activeSettingsTab = ref<"general" | "advanced">("general");
const settingsTabsRoot = ref<HTMLElement | null>(null);
const showCode = ref(false);
const isForm = computed(() => !!store.formId);
const previewSettings = computed<EmbedSettings>(() =>
  isForm.value
    ? controls.settings
    : { ...controls.settings, layout: { id: "INLINE" } }
);
const basicEmbedCode = computed(() =>
  buildBasicEmbedCode({
    baseUrl: store.integrate.baseUrl,
    fallbackScriptBaseUrl: getFormEmbedUrlForRegion(store.regionCode),
    isWhiteLabelled: store.integrate.isWhiteLabelled,
    environment: process.env.NODE_ENV,
    widgetName: store.integrateWidget.name,
    widgetId: store.integrateWidget.id,
    checkForCookieConsent: controls.settings.checkForCookieConsent,
    cookieConsentProvider: controls.settings.cookieConsentProvider,
  })
);
const changeSettingsTab = (value: string | number) => {
  activeSettingsTab.value = value === "advanced" ? "advanced" : "general";
};
const syncSettingsTabA11y = () => {
  const root = settingsTabsRoot.value;
  const tabList = root?.querySelector<HTMLElement>(".n-tabs-nav");
  if (!tabList) return;

  tabList.setAttribute("role", "tablist");
  root?.querySelectorAll<HTMLElement>(".n-tabs-tab").forEach((tab) => {
    const name = tab.dataset.name;
    if (!name) return;
    const selected = name === activeSettingsTab.value;
    tab.id = `embed-${name}-tab`;
    tab.setAttribute("role", "tab");
    tab.setAttribute("aria-selected", String(selected));
    tab.setAttribute("aria-controls", `embed-${name}-panel`);
    tab.setAttribute("tabindex", selected ? "0" : "-1");
  });
};
onMounted(syncSettingsTabA11y);
watch(activeSettingsTab, () => nextTick(syncSettingsTabA11y), {
  flush: "post",
});
</script>

<template>
  <section class="embed-pane">
    <EmbedPreview :settings="previewSettings" @open-code="showCode = true" />
    <aside class="embed-pane__settings">
      <div class="embed-pane__header">
        <h2 data-testid="embed-settings-heading">
          {{ t("integrate.embed.settingsTitle") }}
        </h2>
      </div>
      <div v-if="isForm" ref="settingsTabsRoot">
        <UITabs
          :value="activeSettingsTab"
          type="line"
          justify-content="space-around"
          class="embed-pane__tabs"
          @update:value="changeSettingsTab"
        >
          <UITabPane name="general" :tab="t('integrate.embed.general')">
            <div
              id="embed-general-panel"
              role="tabpanel"
              aria-labelledby="embed-general-tab"
            >
              <EmbedGeneralSettings :controls="controls" />
            </div>
          </UITabPane>
          <UITabPane name="advanced" :tab="t('integrate.embed.advanced')">
            <div
              id="embed-advanced-panel"
              role="tabpanel"
              aria-labelledby="embed-advanced-tab"
            >
              <EmbedAdvancedSettings :controls="controls" />
            </div>
          </UITabPane>
        </UITabs>
      </div>
      <div
        v-else
        data-testid="embed-standard-settings"
        class="embed-pane__standard-settings"
      >
        <CookieConsentSettings
          :enabled="controls.settings.checkForCookieConsent"
          :provider="controls.settings.cookieConsentProvider"
          @update:enabled="controls.setCookieConsent($event)"
          @update:provider="
            controls.setCookieConsent(
              controls.settings.checkForCookieConsent,
              $event
            )
          "
        />
      </div>
    </aside>
    <EmbedCodeModal
      v-model:show="showCode"
      :controls="controls"
      :widget-id="store.integrateWidget.id"
      :standard-only="!isForm"
      :basic-embed-code="basicEmbedCode"
    />
  </section>
</template>

<style scoped>
.embed-pane {
  display: flex;
  min-width: 0;
  min-height: 0;
  height: 100%;
  flex: 1;
  gap: 12px;
}
.embed-pane__settings {
  width: var(--integrate-side-panel-width);
  min-width: var(--integrate-side-panel-min-width);
  max-width: var(--integrate-side-panel-max-width);
  flex: 0 0 auto;
  overflow-x: hidden;
  overflow-y: auto;
  border-radius: 12px;
  background: var(--base-white, #fff);
  box-sizing: border-box;
}
.embed-pane__header {
  display: flex;
  min-height: 58px;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 16px;
}
.embed-pane__header h2 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
}
.embed-pane__tabs {
  width: 100%;
  border-bottom: 1px solid var(--gray-200, #eaecf0);
}
.embed-pane__standard-settings {
  min-width: 0;
  padding: 20px;
}
.embed-pane__standard-settings :deep(#cookieConsent > div:last-child) {
  width: 100% !important;
}
@media (max-width: 1024px) {
  .embed-pane {
    flex-direction: column;
    overflow-y: auto;
  }
  .embed-pane :deep(.embed-preview) {
    min-height: 620px;
    flex: 0 0 620px;
  }
  .embed-pane__settings {
    width: 100%;
    min-width: 0;
    max-width: none;
    flex: 0 0 auto;
  }
}
</style>
